import React from "react";
import {CommandBar, ICommandBarItemProps} from "@fluentui/react";
import {useNavigate} from "react-router-dom";
import {IButtonStyles} from "@fluentui/react/lib/Button";
import styles from './Navigation.module.css';
import strings from "../../loc/strings";
import {
    BULK_DELETE_ASSESSMENTS_ROLES,
    CONF_SYS_DETAILS_ROLES, MANAGE_SYS_DATA_CA_CONTACT_LIST_ROLES,
    MANAGE_SYS_DATA_POI_LIST_ROLES,
    MANAGE_SYS_DATA_ROLES,
    NAV_KEY_CA_CONTACT_LIST,
    NAV_KEY_CONF_SYS_DETAILS,
    NAV_KEY_EU_SEC_LEG,
    NAV_KEY_MANAGE_SYS_DATA,
    NAV_KEY_FIELD_GUIDANCE,
    NAV_KEY_PERIODS,
    NAV_KEY_POI_LIST,
    NAV_KEY_POI_SEARCH_SCENARIOS,
    NAV_KEY_REMINDER,
    NAV_KEY_SEARCH_CONTACTS,
    NAV_KEY_SEARCH_POI, NAV_KEY_VIEW_EMAIL_CONFIGURATION,
    NAV_KEY_VIEW_REQUESTS, NAV_KEY_VIEW_UPLOADED_RECORDS_LIST,
    SEARCH_CONTACTS_ROLES,
    SEARCH_POI_ROLES,
    SUB_NAV_KEYS_CONF_SYS_DETAILS,
    SUB_NAV_KEYS_MANAGE_SYS_DATA,
    VIEW_REQUESTS_ROLES,
    NAV_KEY_BULK_DELETE_ASSESSMENTS,
    NAV_KEY_CA_END_USER_ASSIGNMENT,
    CA_END_USER_ASSIGNMENT_ROLES
} from "../../constants/EiopaFnPConstants";

interface NavigationProps {
    userRoles: string[];
    selectedNav?: string;
}

function Navigation(props: NavigationProps) {
    const navigate = useNavigate();

    function handleClick(routePath: string) {
        navigate(routePath);
    }

    const itemStyle: IButtonStyles = {
        root: {
            backgroundColor: '#EFF4F8',
            color: '#201F1E',
            marginTop: '10px',
            marginRight: '30px'
        },
        rootHovered: { backgroundColor: '#C8D3DD' },
        rootExpanded: { backgroundColor: '#C8D3DD' },
        rootExpandedHovered: { backgroundColor: '#C8D3DD' },
        rootPressed: { backgroundColor: '#C8D3DD' },
        rootDisabled: { backgroundColor: '#EFF4F8' }
    }

    const itemSelectedStyle: IButtonStyles = {
        root: {
            backgroundColor: '#C8D3DD',
            color: '#201F1E',
            marginTop: '10px',
            marginRight: '30px'
        },
        rootHovered: { backgroundColor: '#C8D3DD' },
        rootExpanded: { backgroundColor: '#C8D3DD' },
        rootExpandedHovered: { backgroundColor: '#C8D3DD' },
        rootPressed: { backgroundColor: '#C8D3DD' }
    }

    const _items: ICommandBarItemProps[] = [];

    if(props.userRoles.some((role) => VIEW_REQUESTS_ROLES.includes(role))) {
        _items.push(
            {
                key: NAV_KEY_VIEW_REQUESTS,
                text: strings.viewRequests,
                buttonStyles: _setButtonStyles(NAV_KEY_VIEW_REQUESTS, props.selectedNav),
                onClick: () => handleClick('/view-requests')
            }
        );
    }

    if(props.userRoles.some((role) => SEARCH_POI_ROLES.includes(role))) {
        _items.push(
            {
                key: NAV_KEY_SEARCH_POI,
                text: strings.searchPoi,
                buttonStyles: _setButtonStyles(NAV_KEY_SEARCH_POI, props.selectedNav),
                onClick: () => handleClick('/search-poi')
            }
        );
    }

    if(props.userRoles.some((role) => SEARCH_CONTACTS_ROLES.includes(role))) {
        _items.push(
            {
                key: NAV_KEY_SEARCH_CONTACTS,
                text: strings.searchContacts,
                buttonStyles: _setButtonStyles(NAV_KEY_SEARCH_CONTACTS, props.selectedNav),
                onClick: () => handleClick('/search-contacts'),
            }
        );
    }

    if(props.userRoles.some((role) => CONF_SYS_DETAILS_ROLES.includes(role))) {
        _items.push(
            {
                key: NAV_KEY_CONF_SYS_DETAILS,
                text: strings.configureSystemDetails,
                buttonStyles: _setButtonStylesIncluded(SUB_NAV_KEYS_CONF_SYS_DETAILS, props.selectedNav),
                subMenuProps: {
                    items: [
                        {
                            key: NAV_KEY_EU_SEC_LEG,
                            text: strings.euSecLegBases,
                            className: _setClassIfSelected(NAV_KEY_EU_SEC_LEG, props.selectedNav),
                            onClick: () => handleClick('/admin/eu-sec-leg-bases')
                        },
                        {
                            key: NAV_KEY_REMINDER,
                            text: strings.reminder,
                            className: _setClassIfSelected(NAV_KEY_REMINDER, props.selectedNav),
                            onClick: () => handleClick('/admin/reminder')
                        },
                        {
                            key: NAV_KEY_PERIODS,
                            text: strings.periods,
                            className: _setClassIfSelected(NAV_KEY_PERIODS, props.selectedNav),
                            onClick: () => handleClick('/admin/periods')
                        },
                        {
                            key: NAV_KEY_POI_SEARCH_SCENARIOS,
                            text: strings.poiSearchScenarios,
                            className: _setClassIfSelected(NAV_KEY_POI_SEARCH_SCENARIOS, props.selectedNav),
                            onClick: () => handleClick('/admin/poi-search-scenarios')
                        },
                        {
                            key: NAV_KEY_FIELD_GUIDANCE,
                            text: strings.fieldGuidance,
                            className: _setClassIfSelected(NAV_KEY_FIELD_GUIDANCE, props.selectedNav),
                            onClick: () => handleClick('/admin/field-guidance')
                        }
                    ]
                }
            }
        );
    }

    if(props.userRoles.some((role) => MANAGE_SYS_DATA_ROLES.includes(role))) {
        const manageSysDataMenuItem: ICommandBarItemProps =  {
            key: NAV_KEY_MANAGE_SYS_DATA,
            text: strings.manageSystemData,
            buttonStyles: _setButtonStylesIncluded(SUB_NAV_KEYS_MANAGE_SYS_DATA, props.selectedNav),
            subMenuProps: { items: [] }
        }
        if(props.userRoles.some((role) => MANAGE_SYS_DATA_POI_LIST_ROLES.includes(role))) {
            manageSysDataMenuItem.subMenuProps?.items.push({
                key: NAV_KEY_POI_LIST,
                    text: strings.poiList,
                className: _setClassIfSelected(NAV_KEY_POI_LIST, props.selectedNav),
                onClick: () => handleClick('/manage-system-data/poi-list')
            });
        }
        if(props.userRoles.some((role) => MANAGE_SYS_DATA_CA_CONTACT_LIST_ROLES.includes(role))) {
            manageSysDataMenuItem.subMenuProps?.items.push({
                key: NAV_KEY_CA_CONTACT_LIST,
                text: strings.caContactList,
                className: _setClassIfSelected(NAV_KEY_CA_CONTACT_LIST, props.selectedNav),
                onClick: () => handleClick('/manage-system-data/ca-contact-list')
            });
        }
        if(props.userRoles.some((role) => MANAGE_SYS_DATA_POI_LIST_ROLES.includes(role))) {
            manageSysDataMenuItem.subMenuProps?.items.push({
                key: NAV_KEY_VIEW_UPLOADED_RECORDS_LIST,
                text: strings.listOfFilesUploaded,
                className: _setClassIfSelected(NAV_KEY_VIEW_UPLOADED_RECORDS_LIST, props.selectedNav),
                onClick: () => handleClick('/manage-system-data/view-uploaded-records')
            });
        }
        if(props.userRoles.some((role) => MANAGE_SYS_DATA_POI_LIST_ROLES.includes(role))) {
            manageSysDataMenuItem.subMenuProps?.items.push({
                key: NAV_KEY_VIEW_EMAIL_CONFIGURATION,
                text: strings.emailConfiguration,
                className: _setClassIfSelected(NAV_KEY_VIEW_EMAIL_CONFIGURATION, props.selectedNav),
                onClick: () => handleClick('/manage-system-data/email-configuration')
            });
        }
        
        if(props.userRoles.some((role) => CA_END_USER_ASSIGNMENT_ROLES.includes(role))) {
            manageSysDataMenuItem.subMenuProps?.items.push({
                key: NAV_KEY_CA_END_USER_ASSIGNMENT,
                text: strings.caEndUserAssignment,
                className: _setClassIfSelected(NAV_KEY_CA_END_USER_ASSIGNMENT, props.selectedNav),
                onClick: () => handleClick('/manage-system-data/eu-legal-basis-assignments')
            });
        }

        if (props.userRoles.some((role) => BULK_DELETE_ASSESSMENTS_ROLES.includes(role))) {
            manageSysDataMenuItem.subMenuProps?.items.push(
                {
                    key: NAV_KEY_BULK_DELETE_ASSESSMENTS,
                    text: strings.bulkDeleteAssessments,
                    buttonStyles: _setButtonStyles("NAV_KEY_BULK_DELETE_ASSESSMENTS", props.selectedNav),
                    onClick: () => handleClick('/bulk-delete-assessments')
                }
            )
        }
        _items.push(manageSysDataMenuItem);
    }

    function _setButtonStyles(currentNav: string, selectedNav: string | undefined) {
        if (selectedNav && selectedNav === currentNav) {
            return itemSelectedStyle;
        }
        return itemStyle;
    }

    function _setButtonStylesIncluded(currentNav: string[], selectedNav: string | undefined) {
        if (selectedNav && currentNav.includes(selectedNav)) {
            return itemSelectedStyle;
        }
        return itemStyle;
    }

    return (
        <CommandBar
            items={_items}
            ariaLabel="Inbox actions"
            primaryGroupAriaLabel="Email actions"
        />
    );
}

function _setClassIfSelected(currentNav: string, selectedNav: string | undefined): string {
    if (selectedNav && selectedNav === currentNav) {
        return styles.SubMenuItemSelected;
    }
    return "";
}

export default Navigation;
